import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Button, Typography, Tag, Tooltip, Alert } from "antd";
import { PlusOutlined, EditOutlined, LinkOutlined, ReloadOutlined } from "@ant-design/icons";
import { api } from "../../services/api";
import { resolveImgUrl } from "../../templates/4/utils";
import { hexAlpha } from "../../utils/colorUtils";

const { Text } = Typography;

const PRIMARY = "#4f46e5";

const CircleBtn = ({ icon, onClick, title, danger }) => (
  <Tooltip title={title}>
    <button
      type="button"
      onClick={onClick}
      style={{
        width: 32, height: 32,
        border: `1px solid ${danger ? "#fca5a5" : "#e5e7eb"}`,
        borderRadius: "50%", background: "#fff",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        cursor: "pointer", color: danger ? "#ef4444" : "#374151",
        fontSize: 14, flexShrink: 0, transition: "border-color 0.15s",
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = danger ? "#ef4444" : "#374151"; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = danger ? "#fca5a5" : "#e5e7eb"; }}
    >
      {icon}
    </button>
  </Tooltip>
);

export const Dashboard = () => {
  const [tiendas, setTiendas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const cargar = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.platform.tiendas.list();
      if (res.status === "success") setTiendas(res.data || []);
      else setError(res.message || "No se pudieron cargar las tiendas.");
    } catch { setError("Error de red o servidor."); }
    setLoading(false);
  };

  useEffect(() => { cargar(); }, []);

  const columns = [
    {
      title: "Tienda", key: "nombre", minWidth: 200,
      render: (_, t) => {
        const nombre = t.display_name || t.nombre || "—";
        const initials = nombre.slice(0, 2).toUpperCase();
        const imgSrc = t.favicon || t.logo_dark || t.logo_desktop || t.logo_mobile || null;
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8, flexShrink: 0,
              background: hexAlpha(PRIMARY, 0.10),
              display: "flex", alignItems: "center", justifyContent: "center",
              color: PRIMARY, fontWeight: 700, fontSize: 13, overflow: "hidden",
            }}>
              {imgSrc ? (
                <img
                  src={resolveImgUrl(imgSrc)}
                  alt={nombre}
                  style={{ width: "80%", height: "80%", objectFit: "contain" }}
                  onError={e => { e.target.style.display = "none"; }}
                />
              ) : initials}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 14, color: "#111827" }}>{nombre}</div>
              <div style={{ fontSize: 12, color: "#9ca3af" }}>{t.slug}</div>
            </div>
          </div>
        );
      },
    },
    {
      title: "ERP", dataIndex: "erp_type", key: "erp", width: 120,
      render: (v) => <Text type="secondary" style={{ fontSize: 13 }}>{v || "—"}</Text>,
    },
    {
      title: "Estado", key: "estado", width: 100,
      render: (_, t) => (
        <Tag color={t.activa ? "success" : "default"} style={{ borderRadius: 6 }}>
          {t.activa ? "Activa" : "Inactiva"}
        </Tag>
      ),
    },
    {
      title: "", key: "acciones", width: 80, align: "center",
      render: (_, t) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          <CircleBtn icon={<EditOutlined />} title="Editar" onClick={(e) => { e.stopPropagation(); navigate(`/platform/tiendas/${t.slug}`); }} />
          <CircleBtn icon={<LinkOutlined />} title="Abrir tienda" onClick={(e) => { e.stopPropagation(); window.open(`/${t.slug}`, "_blank"); }} />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <Text strong style={{ fontSize: 28, lineHeight: 1.2 }}>Tiendas</Text>
        <div style={{ display: "flex", gap: 8 }}>
          <Tooltip title="Actualizar">
            <Button type="text" icon={<ReloadOutlined />} onClick={cargar} loading={loading} />
          </Tooltip>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/platform/tiendas/nueva")}
            style={{ background: PRIMARY }}
          >
            Nueva tienda
          </Button>
        </div>
      </div>

      {!loading && !error && (
        <Text type="secondary" style={{ fontSize: 13, marginBottom: 12, display: "block" }}>
          {tiendas.length} tienda{tiendas.length !== 1 ? "s" : ""}
        </Text>
      )}

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Table
        columns={columns}
        dataSource={tiendas}
        rowKey="id"
        loading={loading}
        size="middle"
        scroll={{ x: "max-content" }}
        onRow={(t) => ({ onClick: () => navigate(`/platform/tiendas/${t.slug}`), style: { cursor: "pointer" } })}
        locale={{ emptyText: "No hay tiendas cargadas todavía." }}
        pagination={{ pageSize: 20, showSizeChanger: false, style: { padding: "8px 16px" } }}
        style={{ background: "#fff", borderRadius: 8 }}
      />
    </div>
  );
};
